import classNames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, LabelM } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import background from './assets/background.jpg';
import players from './assets/players.png';

const Root = styled.div`
  padding: 0 var(--triple-unit);
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  grid-template-areas: 'left center right';
  background-image: url(${background});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  gap: var(--quadruple-unit);

  &.rounded {
    border-radius: var(--double-unit);
  }
`;
const PlayersWrapper = styled.div`
  grid-area: left;
  justify-self: center;
  position: relative;
  overflow: hidden;
  width: 100%;
`;
const PlayersImage = styled.img`
  display: none;
  position: absolute;
  top: var(--intermediate-unit);
  left: 50%;
  transform: translateX(-50%);
  height: auto;
  max-height: 120%;
  max-width: 100%;

  &.tall {
    top: var(--double-and-a-half-unit);
  }

  @media ${laptopAndAbove} {
    display: block;
  }
`;

const TitleWrapper = styled(Vertical).attrs({ gap: 2, center: true })`
  grid-area: center;
  padding: var(--double-unit) 0;

  &.tall {
    padding-top: var(--quadruple-unit);
  }
`;
const IconWrapper = styled.div`
  grid-area: right;
  align-self: center;
  justify-self: flex-end;
`;

type Props = {
  seasonName: string;
  rounded?: boolean;
  tall?: boolean;
  showPlayers?: boolean;
  to?: string;
  children?: ReactNode;
};
export const HallOfFameBanner = ({
  seasonName,
  rounded,
  tall,
  showPlayers = true,
  to,
  children,
}: Props) => {
  return (
    <ConditionalWrapper wrap={!!to} Wrapper={Link} props={{ to: to! }}>
      <Root className={classNames({ rounded })}>
        <PlayersWrapper>
          {!!showPlayers && (
            <PlayersImage
              className={classNames({ tall })}
              src={players}
              alt=""
            />
          )}
        </PlayersWrapper>
        <TitleWrapper className={classNames('text-center', { tall })}>
          <Vertical center gap={0}>
            <BrandM>
              <FormattedMessage {...fantasy.hallOfFame} />
            </BrandM>
            <LabelM>
              <FormattedMessage
                id="HallOfFameBanner.subtitle"
                defaultMessage="Your {seasonName} Best Lineups"
                values={{ seasonName }}
              />
            </LabelM>
          </Vertical>
          {children}
        </TitleWrapper>
        {!!to && (
          <IconWrapper>
            <ChevronRightBold />
          </IconWrapper>
        )}
      </Root>
    </ConditionalWrapper>
  );
};
